// TREASURE BOX - Judge.me reviewer name privacy mask
// Masks full Korean reviewer names shown by the Judge.me app widgets to
// "surname + first given-name char + *" (e.g. 권순천 -> 권순*) to avoid
// exposing full personal names on product pages / review widgets.
//
// NOTE: Judge.me is currently mid-transition ("dual publish") between its
// legacy widget markup (hidden, class names like .jdgm-rev__author) and its
// new 2025 "revamp" widget (Vue-based, class names like
// .jm-reviewer-info__name, .jdgm-review-card__reviewer-name,
// .jdgm-rev-snippet-card__reviewer-name). Both sets of classes are covered
// below, plus a generic fallback and a JSON-LD structured-data patch so the
// mask keeps working even if Judge.me renames things again.
(function () {
  function maskKoreanName(text) {
    if (!text) return null;
    var trimmed = ('' + text).trim();
    // Only touch plausible Korean full names (2-6 Hangul syllables),
    // and skip anything already masked with an asterisk.
    if (/^[가-힣]{2,6}$/.test(trimmed)) {
      return trimmed.slice(0, 2) + '*';
    }
    return null;
  }

  // Known Judge.me reviewer-name element classes (legacy + 2025 revamp +
  // grid + snippet-carousel widgets), plus a generic "contains jdgm/jm and
  // author/reviewer-name" fallback for anything not explicitly listed.
  var SELECTOR = [
    '.jdgm-rev__author',
    '.jdgm-full-rev__author',
    '.jdgm-rev-snippet-card__author',
    '.jdgm-rev-snippet-card__reviewer-name',
    '.jdgm-review-card__reviewer-name',
    '.jm-reviewer-info__name',
    '.jm-reviewer-info__name-cluster',
    '[class*="reviewer-name"]',
    '[class*="reviewer_name"]',
    '[class*="jdgm"] [class*="author"]',
    '[class*="jm-"][class*="reviewer"]'
  ].join(',');

  function maskElement(el) {
    if (!el || (el.dataset && el.dataset.tbMasked === '1')) return;

    // The revamp widget sets both the visible text (innerHTML/textContent)
    // AND a `title` attribute to the full reviewer name - both must be
    // masked or the full name still leaks via tooltip/DOM inspection.
    var textMasked = maskKoreanName(el.textContent);
    var titleMasked = el.getAttribute ? maskKoreanName(el.getAttribute('title')) : null;

    if (textMasked || titleMasked) {
      if (textMasked) el.textContent = textMasked;
      if (titleMasked) el.setAttribute('title', titleMasked);
      else if (textMasked && el.getAttribute && el.getAttribute('title')) {
        // keep title attribute consistent even if it wasn't a pure Korean
        // name match by itself (defensive - rare case)
        el.setAttribute('title', textMasked);
      }
      if (el.dataset) el.dataset.tbMasked = '1';
    }
  }

  function maskAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll(SELECTOR);
    els.forEach(maskElement);

    // Generic fallback: any element anywhere inside a Judge.me widget
    // container whose OWN text is exactly a plausible Korean full name
    // (2-6 Hangul syllables, no children) - catches future class renames.
    var containers = scope.querySelectorAll('[class*="jdgm"], [class*="jm-"]');
    containers.forEach(function (container) {
      if (container.children && container.children.length) return; // only leaf-ish nodes
      maskElement(container);
    });
  }

  // Judge.me also embeds the full reviewer name in a JSON-LD structured
  // data script tag (schema.org Review/author) for SEO - mask that too so
  // the name doesn't leak via page source / search engines.
  function maskJsonLd() {
    var scripts = document.querySelectorAll('script.jdgm-aggregate-rating-jld, script[type="application/ld+json"]');
    scripts.forEach(function (script) {
      if (script.dataset && script.dataset.tbMasked === '1') return;
      var raw = script.textContent;
      if (!raw || raw.indexOf('"author"') === -1) return;
      try {
        var data = JSON.parse(raw);
        var changed = false;
        var reviews = data && data.review;
        if (Array.isArray(reviews)) {
          reviews.forEach(function (r) {
            if (r && r.author && typeof r.author.name === 'string') {
              var masked = maskKoreanName(r.author.name);
              if (masked) {
                r.author.name = masked;
                changed = true;
              }
            }
          });
        }
        if (changed) {
          script.textContent = JSON.stringify(data);
          if (script.dataset) script.dataset.tbMasked = '1';
        }
      } catch (e) {
        // not valid/relevant JSON - ignore
      }
    });
  }

  // Deepest, most robust layer: patch the raw review-data object that
  // Judge.me's Vue "revamp" widget itself reads from (window.jdgm.data.*),
  // BEFORE it renders. This also fixes the reviewer avatar's `alt` text
  // (which embeds the full name) and stays correct even if Judge.me
  // renames its CSS classes again in the future.
  function maskDataObject(obj, seen) {
    if (!obj || typeof obj !== 'object') return;
    seen = seen || new WeakSet();
    if (seen.has(obj)) return;
    seen.add(obj);
    if (Array.isArray(obj)) {
      for (var i = 0; i < obj.length; i++) maskDataObject(obj[i], seen);
      return;
    }
    if (typeof obj.reviewer_name === 'string') {
      var masked = maskKoreanName(obj.reviewer_name);
      if (masked) obj.reviewer_name = masked;
    }
    for (var key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      var val = obj[key];
      if (val && typeof val === 'object') maskDataObject(val, seen);
    }
  }

  function maskJdgmData() {
    try {
      var d = window.jdgm && window.jdgm.data;
      if (!d) return;
      maskDataObject(d.reviewWidget);
      maskDataObject(d.reviewWidgetRevamp);
      maskDataObject(d.allReviewsWidgetV2025);
      maskDataObject(d.reviewsGridWidget);
    } catch (e) {
      // window.jdgm not ready yet or unexpected shape - ignore, DOM-based
      // masking below still covers this case.
    }
  }

  function runMask() {
    maskJdgmData();
    maskAll();
    maskJsonLd();
  }

  var scheduled = false;
  function scheduleMask() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      runMask();
    });
  }

  document.addEventListener('DOMContentLoaded', runMask);

  // Judge.me widgets (product widget, snippet carousel, popup, full-review
  // modal, grid widget) render asynchronously via Vue and re-render on
  // interaction (sorting, pagination, tab switch), so keep watching the DOM
  // for newly added / re-rendered review markup indefinitely.
  var observer = new MutationObserver(scheduleMask);
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['title'] });

  // Safety net in case DOMContentLoaded already fired before this script ran.
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    runMask();
  }

  // Extra safety: Judge.me's Vue widgets can take a moment to hydrate even
  // after DOM nodes appear, so re-run a few times on a short schedule too.
  var retries = 0;
  var interval = setInterval(function () {
    runMask();
    retries++;
    if (retries >= 20) clearInterval(interval); // ~20s of periodic re-checks
  }, 1000);
})();
